import type { SchemaProperty } from 'types/schema';

export interface NumberRuleConstraints {
  min?: number;
  max?: number;
  integer: boolean;
  precision?: number;
}

/**
 * Reads numeric constraint rules off a schema property — shared by every `number`/`unit` render
 * surface so `min`/`max`/`integer`/`precision` validation stays identical regardless of which
 * molecule renders the field. A code-defined `TYPE_DEFAULT_RENDERERS` entry has no way to express
 * these (they're per-property, from `rules`, not static `RendererRef.props`), so callers pass
 * this as `UnifiedPropertyRendererMolecule`'s `extraProps` (EAINT-855 Phase 4; previously duplicated in
 * `AdditionalPropertiesGrid`/`SphereFieldInputMolecule`).
 */
export function numberRuleConstraints(property: SchemaProperty): NumberRuleConstraints {
  const rules = property.rules ?? [];
  const minRule = rules.find((r) => r.method === 'min');
  const maxRule = rules.find((r) => r.method === 'max');
  const positive = rules.some((r) => r.method === 'positive');
  const integer = rules.some((r) => r.method === 'integer');
  const precisionRule = rules.find((r) => r.method === 'precision');
  let min: number | undefined = minRule?.args?.[0];
  if (positive && (min === undefined || min < 0)) min = 0;
  return {
    min,
    max: maxRule?.args?.[0] as number | undefined,
    integer,
    precision: precisionRule?.args?.[0] as number | undefined,
  };
}
